import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader } from "../../components/app/Common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { 
  PlusCircle, Trash2, Wrench, Copy, Pencil, 
  ChevronDown, ChevronRight, ChevronUp, Archive, Layers, Search,
  Plus, RotateCcw
} from "lucide-react";

const emptyTpl = { title: "", spec_section: "", trade: "Waterproofing", description: "", revision: 1, active: true };

const RESPONSE_TYPES = [
  { value: "Pass/Fail", label: "Pass / Fail" },
  { value: "Yes/No", label: "Yes / No" },
  { value: "Photo", label: "Photo / Attachment" },
  { value: "Signature", label: "Signature" },
  { value: "Text", label: "Text Field" },
  { value: "Number", label: "Numeric Value" },
  { value: "Date", label: "Date" },
];

export default function AdminITPTemplates() {
  const [templates, setTemplates] = useState([]);
  const [deletedTemplates, setDeletedTemplates] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [openTpl, setOpenTpl] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [openClone, setOpenClone] = useState(null);
  const [projects, setProjects] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/itp/templates", { params: { include_deleted: true } }); // [lixeira-itp]
      const activeList = data.filter(t => !t.is_deleted && !t.deleted_at);
      const trashList = data.filter(t => t.is_deleted || t.deleted_at);
      
      setTemplates(activeList);
      setDeletedTemplates(trashList);
      
      if (activeList.length > 0 && Object.keys(expandedTemplates).length === 0) {
        setExpandedTemplates({ [activeList[0].id]: true });
      }
    } catch (e) {
      toast.error("Failed to load templates");
    }
  }, [expandedTemplates]);

  useEffect(() => {
    load();
    api.get("/jobsites").then(({ data }) => setProjects(data)).catch(() => {});
  }, [load]);

  const filteredTemplates = useMemo(() => {
    const list = showTrash ? deletedTemplates : templates;
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.spec_section && t.spec_section.toLowerCase().includes(q))
    );
  }, [templates, deletedTemplates, showTrash, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showTrash]);

  const paginatedTemplates = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTemplates.slice(start, start + itemsPerPage);
  }, [filteredTemplates, currentPage, itemsPerPage]);

  const toggleExpand = (id) => {
    setExpandedTemplates(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const openNew = () => { setEditingTpl({ ...emptyTpl }); setOpenTpl(true); };
  const openEdit = (t) => { setEditingTpl({ ...t }); setOpenTpl(true); };

  const saveTemplate = async () => {
    try {
      if (editingTpl.id) {
        await api.put(`/itp/templates/${editingTpl.id}`, {
          title: editingTpl.title,
          spec_section: editingTpl.spec_section,
          trade: editingTpl.trade,
          description: editingTpl.description,
          revision: Number(editingTpl.revision) || 1,
          active: !!editingTpl.active,
        });
      } else {
        await api.post("/itp/templates", {
          title: editingTpl.title,
          spec_section: editingTpl.spec_section,
          trade: editingTpl.trade,
          description: editingTpl.description,
          revision: Number(editingTpl.revision) || 1,
          active: !!editingTpl.active,
        });
      }
      toast.success("Template saved");
      setOpenTpl(false);
      setEditingTpl(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Move this template to trash?")) return;
    try {
      await api.delete(`/itp/templates/${id}`);
      toast.success("Template moved to trash");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // [lixeira-itp] Tirar da lixeira: o template volta para a lista normal, com tudo o que tinha.
  const restore = async (id) => {
    try {
      await api.post(`/itp/templates/${id}/restore`);
      toast.success("Template restored");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };

  // Apagar de vez (so de dentro da lixeira). Nao tem volta.
  const deleteForever = async (tpl) => {
    if (!confirm(`Delete "${tpl.title}" forever? This cannot be undone.`)) return;
    try {
      await api.delete(`/itp/templates/${tpl.id}/permanent`);
      toast.success("Template deleted forever");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const quickAddSection = async (tplId) => {
    setExpandedTemplates(prev => ({ ...prev, [tplId]: true }));
    try {
      await api.post(`/itp/templates/${tplId}/items`, {
        item_type: "Section Header",
        title: "New section",
        description: "",
        parent_item_id: null,
      });
      toast.success("New section added!");
      await load();
    } catch (e) {
      toast.error("Failed to add section");
    }
  };

  const quickAddItem = async (tplId, parentSectionId) => {
    setExpandedTemplates(prev => ({ ...prev, [tplId]: true }));
    try {
      await api.post(`/itp/templates/${tplId}/items`, {
        item_type: "Inspection Checkpoint",
        title: "New item",
        parent_item_id: parentSectionId,
        checkpoint_type: "Review Point",
        response_type: "Pass/Fail",
      });
      toast.success("New item added!");
      await load();
    } catch (e) {
      toast.error("Failed to add item");
    }
  };

  const quickAddSubItem = async (tplId, parentItemId) => {
    setExpandedTemplates(prev => ({ ...prev, [tplId]: true }));
    try {
      await api.post(`/itp/templates/${tplId}/items`, {
        item_type: "Inspection Checkpoint",
        title: "New sub-item",
        parent_item_id: parentItemId,
        checkpoint_type: "Review Point",
        response_type: "Pass/Fail",
      });
      toast.success("New sub-item added!");
      await load();
    } catch (e) {
      toast.error("Failed to add sub-item");
    }
  };

  const updateItemTitle = async (tplId, item, newTitle) => {
    if (!newTitle.trim() || newTitle === item.title) return;
    try {
      await api.put(`/itp/templates/${tplId}/items/${item.id}`, {
        ...item,
        title: newTitle,
      });
      toast.success("Updated");
      await load();
    } catch (e) {
      toast.error("Failed to update title");
    }
  };

  const updateResponseType = async (tplId, item, newType) => {
    try {
      await api.put(`/itp/templates/${tplId}/items/${item.id}`, {
        ...item,
        response_type: newType,
      });
      toast.success("Response type updated");
      await load();
    } catch (e) {
      toast.error("Failed to update response type");
    }
  };

  const moveItemPriority = async (tplId, item, siblings, direction) => {
    const currentIndex = siblings.findIndex(s => s.id === item.id);
    if (currentIndex === -1) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= siblings.length) return;

    const newSiblings = [...siblings];
    const [moved] = newSiblings.splice(currentIndex, 1);
    newSiblings.splice(targetIndex, 0, moved);

    setTemplates(prevTemplates =>
      prevTemplates.map(tpl => {
        if (tpl.id !== tplId) return tpl;
        const updatedItems = (tpl.items || []).map(it => {
          const sIdx = newSiblings.findIndex(s => s.id === it.id);
          if (sIdx !== -1) {
            return { ...it, sequence: sIdx + 1, order: sIdx + 1, position: sIdx + 1 };
          }
          return it;
        });
        return { ...tpl, items: updatedItems };
      })
    );

    try {
      const updates = newSiblings.map((it, idx) =>
        api.put(`/itp/templates/${tplId}/items/${it.id}`, {
          ...it,
          sequence: idx + 1,
          order: idx + 1,
          position: idx + 1,
        })
      );
      await Promise.all(updates);
      toast.success("Order updated");
    } catch (e) {
      toast.error("Failed to update order");
      await load();
    }
  };

  const deleteItem = async (tplId, itemId) => {
    try {
      await api.delete(`/itp/templates/${tplId}/items/${itemId}`);
      toast.success("Item deleted");
      await load();
    } catch (e) {
      toast.error("Failed to delete item");
    }
  };

  // Botoes do topo (Lixeira e Novo template). Usados no cabecalho do computador e no do celular.
  const headerButtons = (
    <>
      <Button
        variant="outline"
        onClick={() => setShowTrash(!showTrash)}
        className={`h-12 rounded-lg border-border text-base md:h-9 md:rounded-sm md:text-sm ${showTrash ? "bg-muted" : ""}`}
      >
        <Archive className="mr-1.5 h-4 w-4" />
        {showTrash ? "View Active" : `Trash (${deletedTemplates.length})`}
      </Button>
      <Button
        onClick={openNew}
        className="h-12 rounded-lg bg-primary text-base hover:bg-primary/90 md:h-9 md:rounded-sm md:text-sm"
      >
        <PlusCircle className="mr-1.5 h-4 w-4" /> New template
      </Button>
    </>
  );

  return (
    <div className="mx-auto max-w-full space-y-5 px-4 pb-10 pt-5 md:space-y-6 md:p-10">
      {/* Computador: o cabecalho de sempre */}
      <div className="hidden md:block">
        <PageHeader
          title="ITP Templates"
          subtitle="Design digital Inspection & Test Plans with checkpoint types and response types. Clone them to projects for field execution."
          action={<div className="flex gap-2">{headerButtons}</div>}
        />
      </div>

      {/* Celular: titulo e texto em cima, botoes lado a lado embaixo */}
      <div className="md:hidden">
        <h1 className="text-3xl font-bold tracking-tight">ITP Templates</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Design digital Inspection &amp; Test Plans, then clone them to projects for field work.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3">{headerButtons}</div>
      </div>

      <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 shadow-sm md:flex-row md:items-center md:justify-between md:gap-4 md:rounded-sm">
        <div className="relative md:flex-1">
          <Search className="absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground md:left-3 md:h-4 md:w-4" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or section..."
            className="h-12 rounded-lg border-border bg-background pl-11 text-base focus-visible:ring-1 md:h-9 md:rounded-sm md:pl-9 md:text-sm"
          />
        </div>
        <div className="px-1 text-xs text-muted-foreground md:shrink-0 md:px-0">
          Showing {filteredTemplates.length} {showTrash ? "trash" : "active"} item(s)
        </div>
      </div>

      {showTrash && (
        <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground md:rounded-sm" data-testid="trash-note">
          {deletedTemplates.length === 0
            ? "The trash is empty. Templates you delete are kept here until you delete them forever."
            : "Templates in the trash cannot be cloned or edited. Restore one to use it again, or delete it forever."}
        </div>
      )}

      <div className="space-y-3">
        {paginatedTemplates.map((tpl) => (
          <div
            key={`card-${tpl.id}`}
            className="rounded-xl border border-border bg-card p-4 md:flex md:items-center md:justify-between md:gap-4 md:rounded-sm"
          >
            <div className="flex min-w-0 items-start gap-3 md:items-center">
              <Wrench className="mt-1 h-5 w-5 shrink-0 text-muted-foreground md:mt-0 md:h-4 md:w-4" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <span className="break-words text-base font-semibold leading-snug md:text-sm">{tpl.title}</span>
                  <Badge variant="outline" className="shrink-0 whitespace-nowrap font-mono text-[10px]">
                    REV {tpl.revision}
                  </Badge>
                  {tpl.deleted_at ? (
                    <Badge className="shrink-0 whitespace-nowrap bg-destructive/10 text-[10px] text-destructive">IN TRASH</Badge>
                  ) : (
                    tpl.active && (
                      <Badge className="shrink-0 whitespace-nowrap bg-emerald-500/10 text-[10px] text-emerald-500">ACTIVE</Badge>
                    )
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground md:mt-0.5">
                  {tpl.spec_section || "no spec section"} • {tpl.items?.length || 0} items
                  {tpl.deleted_at ? ` • deleted ${new Date(tpl.deleted_at).toLocaleDateString()}` : ""}
                </p>
              </div>
            </div>

            {showTrash ? (
              <div className="mt-4 flex items-center gap-2 md:mt-0 md:shrink-0">
                <Button
                  size="sm"
                  onClick={() => restore(tpl.id)}
                  className="h-12 flex-1 bg-primary text-base hover:bg-primary/90 md:h-8 md:flex-none md:text-xs"
                  data-testid={`restore-${tpl.id}`}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4 md:mr-1 md:h-3.5 md:w-3.5" /> Restore
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteForever(tpl)}
                  className="h-12 flex-1 text-base text-destructive md:h-8 md:flex-none md:text-xs"
                  data-testid={`purge-${tpl.id}`}
                >
                  <Trash2 className="mr-1.5 h-4 w-4 md:mr-1 md:h-3.5 md:w-3.5" /> Delete forever
                </Button>
              </div>
            ) : (
            <div className="mt-4 flex items-center gap-2 md:mt-0 md:shrink-0">
              <Button
                size="sm"
                onClick={() => setOpenClone(tpl)}
                className="h-12 flex-1 bg-primary text-base hover:bg-primary/90 md:h-8 md:flex-none md:text-xs"
              >
                <Copy className="mr-1.5 h-4 w-4 md:mr-1 md:h-3.5 md:w-3.5" /> Clone to project
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEdit(tpl)}
                aria-label="Edit template"
                className="h-12 w-12 p-0 md:h-8 md:w-8"
              >
                <Pencil className="h-4 w-4 md:h-3.5 md:w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => remove(tpl.id)}
                aria-label="Move to trash"
                className="h-12 w-12 p-0 text-destructive md:h-8 md:w-8"
              >
                <Trash2 className="h-4 w-4 md:h-3.5 md:w-3.5" />
              </Button>
            </div>
            )}
          </div>
        ))}
      </div>

      {!showTrash && (
      <>
      <div className="text-xs font-mono font-bold text-muted-foreground tracking-wider uppercase pt-2 flex items-center gap-2">
        <Layers className="h-3.5 w-3.5" /> Detailed Inspection Workflows
      </div>

      <div className="space-y-4 rounded-xl border border-border bg-card/40 p-3 md:rounded-sm md:p-4">
        <div className="text-sm font-semibold tracking-tight flex items-center justify-between border-b border-border/60 pb-3">
          <span>Inspection Items Overview</span>
          <span className="hidden text-xs text-muted-foreground md:inline">Scrollable Workspace</span>
        </div>

        <div className="space-y-3 md:max-h-[620px] md:space-y-4 md:overflow-y-auto md:pr-1">
          {paginatedTemplates.map((tpl) => {
            const isExpanded = !!expandedTemplates[tpl.id];
            const tree = buildTree(tpl.items || []);

            return (
              <div key={`workspace-${tpl.id}`} className="overflow-hidden rounded-xl border border-border bg-card md:rounded-sm">
                <button
                  onClick={() => toggleExpand(tpl.id)}
                  className="flex w-full flex-col items-start gap-1.5 border-b border-border/50 bg-card p-3.5 text-left transition-colors hover:bg-accent/40 md:flex-row md:items-center md:justify-between md:gap-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 shrink-0 text-amber-500 md:h-4 md:w-4" />
                    ) : (
                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground md:h-4 md:w-4" />
                    )}
                    <span className="break-words text-base font-semibold md:text-sm">{tpl.title}</span>
                    <Badge variant="outline" className="shrink-0 whitespace-nowrap font-mono text-[10px] md:ml-2">
                      Rev {tpl.revision}
                    </Badge>
                  </div>
                  <span className="pl-7 text-xs text-muted-foreground md:pl-0">
                    {tpl.items?.length || 0} inspection checkpoints
                  </span>
                </button>

                {isExpanded && (
                  <div className="space-y-3 bg-background/30 p-2.5 md:space-y-4 md:p-4">
                    {tree.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 px-3 py-6 md:rounded-sm">
                        <span className="text-center text-xs text-muted-foreground">No sections added to this template yet.</span>
                        <Button
                          size="sm"
                          onClick={() => quickAddSection(tpl.id)}
                          className="h-11 w-full rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm text-amber-500 transition-all hover:bg-amber-500/20 md:h-8 md:w-auto md:rounded-sm md:text-xs"
                        >
                          Add Section
                        </Button>
                      </div>
                    ) : (
                      tree.map((sectionNode, secIdx) => (
                        <SectionComponent
                          key={sectionNode.id}
                          tplId={tpl.id}
                          sectionNode={sectionNode}
                          secNumber={`${secIdx + 1}`}
                          secIdx={secIdx}
                          siblings={tree}
                          isCollapsed={!!collapsedSections[sectionNode.id]}
                          onToggleCollapse={() => toggleSectionCollapse(sectionNode.id)}
                          onUpdateTitle={(val) => updateItemTitle(tpl.id, sectionNode, val)}
                          onMovePriority={(dir) => moveItemPriority(tpl.id, sectionNode, tree, dir)}
                          onDeleteItem={() => deleteItem(tpl.id, sectionNode.id)}
                          onAddItem={() => quickAddItem(tpl.id, sectionNode.id)}
                          onAddSubItem={(parentId) => quickAddSubItem(tpl.id, parentId)}
                          onUpdateSubTitle={(node, val) => updateItemTitle(tpl.id, node, val)}
                          onUpdateSubResponseType={(node, val) => updateResponseType(tpl.id, node, val)}
                          onMoveSubPriority={(node, sibs, dir) => moveItemPriority(tpl.id, node, sibs, dir)}
                          onDeleteSubItem={(id) => deleteItem(tpl.id, id)}
                        />
                      ))
                    )}

                    {tree.length > 0 && (
                      <div className="flex justify-center pt-1">
                        <Button
                          size="sm"
                          onClick={() => quickAddSection(tpl.id)}
                          className="h-11 w-full rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm text-amber-500 transition-all hover:bg-amber-500/20 md:h-8 md:w-auto md:rounded-sm md:text-xs"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Section
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </>
      )}

      {openTpl && editingTpl && (
        <Dialog open onOpenChange={(o) => { if (!o) { setOpenTpl(false); setEditingTpl(null); } }}>
          <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-xl md:rounded-sm">
            <DialogHeader>
              <DialogTitle>{editingTpl.id ? "Edit ITP Template" : "New ITP Template"}</DialogTitle>
              <DialogDescription>Fill in the template details below.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-sm md:text-xs">Title</Label>
                <Input
                  value={editingTpl.title}
                  onChange={(e) => setEditingTpl({ ...editingTpl, title: e.target.value })}
                  placeholder="e.g. Waterproofing Inspection Plan"
                  className="h-11 rounded-lg text-base md:h-9 md:rounded-sm md:text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm md:text-xs">Spec Section</Label>
                  <Input
                    value={editingTpl.spec_section || ""}
                    onChange={(e) => setEditingTpl({ ...editingTpl, spec_section: e.target.value })}
                    placeholder="e.g. 07 10 00"
                    className="h-11 rounded-lg text-base md:h-9 md:rounded-sm md:text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm md:text-xs">Revision</Label>
                  <Input
                    type="number"
                    value={editingTpl.revision}
                    onChange={(e) => setEditingTpl({ ...editingTpl, revision: e.target.value })}
                    className="h-11 rounded-lg text-base md:h-9 md:rounded-sm md:text-xs"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenTpl(false)} className="h-12 text-base md:h-9 md:text-sm">Cancel</Button>
              <Button onClick={saveTemplate} className="h-12 text-base md:h-9 md:text-sm">Save Template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {openClone && (
        <CloneDialog
          template={openClone}
          projects={projects}
          onClose={() => { setOpenClone(null); load(); }}
        />
      )}
    </div>
  );
}

function SectionComponent({
  tplId,
  sectionNode,
  secNumber,
  secIdx,
  siblings,
  isCollapsed,
  onToggleCollapse,
  onUpdateTitle,
  onMovePriority,
  onDeleteItem,
  onAddItem,
  onAddSubItem,
  onUpdateSubTitle,
  onUpdateSubResponseType,
  onMoveSubPriority,
  onDeleteSubItem,
}) {
  return (
    <div className="space-y-2 overflow-hidden rounded-xl border border-amber-500/30 bg-card/60 pb-2 md:rounded-sm">
      <div className="flex flex-col gap-2 border-b border-amber-500/20 bg-amber-500/5 p-2.5 text-xs md:flex-row md:items-center md:justify-between md:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expand section" : "Collapse section"}
            className="grid h-10 w-10 shrink-0 place-items-center rounded text-amber-500 transition-colors hover:bg-amber-500/10 focus:outline-none md:h-auto md:w-auto md:p-1"
          >
            {isCollapsed ? <ChevronRight className="h-5 w-5 md:h-4 md:w-4" /> : <ChevronDown className="h-5 w-5 md:h-4 md:w-4" />}
          </button>

          <span className="min-w-[1.2rem] shrink-0 font-mono font-bold text-amber-500">
            {secNumber}.
          </span>

          <EditableTitle
            value={sectionNode.title || sectionNode.activity_description || ""}
            onSave={onUpdateTitle}
            className="rounded border border-transparent bg-transparent px-1.5 py-2 text-base font-bold text-foreground hover:border-border hover:bg-background/80 focus:bg-background md:py-0.5 md:text-sm"
          />
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 md:gap-1">
          <div className="flex items-center rounded border border-border bg-background p-0.5">
            <Button
              size="sm"
              variant="ghost"
              disabled={secIdx === 0}
              onClick={() => onMovePriority("up")}
              aria-label="Move section up"
              className="h-10 w-10 p-0 text-muted-foreground hover:text-amber-500 disabled:opacity-30 md:h-6 md:w-6"
            >
              <ChevronUp className="h-5 w-5 md:h-3.5 md:w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={secIdx === siblings.length - 1}
              onClick={() => onMovePriority("down")}
              aria-label="Move section down"
              className="h-10 w-10 p-0 text-muted-foreground hover:text-amber-500 disabled:opacity-30 md:h-6 md:w-6"
            >
              <ChevronDown className="h-5 w-5 md:h-3.5 md:w-3.5" />
            </Button>
          </div>

          <Button
            size="sm"
            variant="ghost"
            onClick={onDeleteItem}
            aria-label="Delete section"
            className="h-10 w-10 p-0 text-muted-foreground hover:text-destructive md:h-7 md:w-7"
          >
            <Trash2 className="h-5 w-5 md:h-3.5 md:w-3.5" />
          </Button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="space-y-2 px-2 pt-1 md:px-3">
          {sectionNode.children && sectionNode.children.map((subNode, subIdx) => (
            <RecursiveItemNode
              key={subNode.id}
              tplId={tplId}
              node={subNode}
              numberPrefix={`${secNumber}.${subIdx + 1}`}
              nodeIdx={subIdx}
              siblings={sectionNode.children}
              sectionId={sectionNode.id}
              onAddItem={onAddItem}
              onAddSubItem={onAddSubItem}
              onUpdateTitle={onUpdateSubTitle}
              onUpdateResponseType={onUpdateSubResponseType}
              onMovePriority={onMoveSubPriority}
              onDeleteItem={onDeleteSubItem}
            />
          ))}

          <div className="mt-2 flex justify-end border-t border-border/30 px-1 pt-2">
            <Button
              size="sm"
              onClick={onAddItem}
              className="h-11 w-full rounded-lg border border-amber-500/30 bg-amber-500/10 text-sm text-amber-500 transition-all hover:bg-amber-500/20 md:h-7 md:w-auto md:rounded-sm md:text-xs"
            >
              Add Item
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecursiveItemNode({
  tplId,
  node,
  numberPrefix,
  nodeIdx,
  siblings,
  sectionId,
  onAddItem,
  onAddSubItem,
  onUpdateTitle,
  onUpdateResponseType,
  onMovePriority,
  onDeleteItem,
}) {
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="space-y-1.5 md:ml-2">
      <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-card p-2.5 text-xs md:flex-row md:items-start md:justify-between md:gap-3 md:rounded-sm md:p-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <span className="min-w-[2.4rem] shrink-0 pt-3 font-mono font-bold text-muted-foreground md:pt-[3px]">
            {numberPrefix}.
          </span>
          <EditableTitle
            value={node.title || node.activity_description || ""}
            onSave={(val) => onUpdateTitle(node, val)}
            className="rounded border border-transparent bg-transparent px-1.5 py-2 text-base text-foreground hover:border-border hover:bg-background focus:bg-background md:py-0.5 md:text-xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:flex-nowrap">
          <select
            value={node.response_type || "Pass/Fail"}
            onChange={(e) => onUpdateResponseType(node, e.target.value)}
            className="h-10 min-w-[8.5rem] flex-1 cursor-pointer rounded-lg border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500 md:h-7 md:flex-none md:rounded-sm md:text-[11px]"
          >
            {RESPONSE_TYPES.map((rt) => (
              <option key={rt.value} value={rt.value}>
                {rt.label}
              </option>
            ))}
          </select>

          <div className="flex items-center rounded border border-border bg-background p-0.5">
            <Button
              size="sm"
              variant="ghost"
              disabled={nodeIdx === 0}
              onClick={() => onMovePriority(node, siblings, "up")}
              aria-label="Move up"
              className="h-9 w-9 p-0 text-muted-foreground hover:text-amber-500 disabled:opacity-30 md:h-5 md:w-5"
            >
              <ChevronUp className="h-5 w-5 md:h-3 md:w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={nodeIdx === siblings.length - 1}
              onClick={() => onMovePriority(node, siblings, "down")}
              aria-label="Move down"
              className="h-9 w-9 p-0 text-muted-foreground hover:text-amber-500 disabled:opacity-30 md:h-5 md:w-5"
            >
              <ChevronDown className="h-5 w-5 md:h-3 md:w-3" />
            </Button>
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onAddItem(sectionId)}
            className="h-10 rounded-lg border-border bg-background px-3 text-xs hover:bg-accent md:h-6 md:rounded-sm md:px-2 md:text-[11px]"
          >
            <Plus className="mr-0.5 h-4 w-4 md:h-3 md:w-3" /> Item
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onAddSubItem(node.id)}
            className="h-10 rounded-lg border-amber-500/40 bg-amber-500/5 px-3 text-xs text-amber-500 hover:bg-amber-500/10 md:h-6 md:rounded-sm md:px-2 md:text-[11px]"
          >
            <Plus className="mr-0.5 h-4 w-4 md:h-3 md:w-3" /> Sub-item
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDeleteItem(node.id)}
            aria-label="Delete item"
            className="ml-auto h-10 w-10 p-0 text-muted-foreground hover:text-destructive md:ml-0 md:h-6 md:w-6"
          >
            <Trash2 className="h-5 w-5 md:h-3.5 md:w-3.5" />
          </Button>
        </div>
      </div>

      {hasChildren && (
        <div className="ml-1 space-y-1.5 border-l-2 border-amber-500/20 pl-2 md:ml-2 md:pl-4">
          {node.children.map((child, childIdx) => (
            <RecursiveItemNode
              key={child.id}
              tplId={tplId}
              node={child}
              numberPrefix={`${numberPrefix}.${childIdx + 1}`}
              nodeIdx={childIdx}
              siblings={node.children}
              sectionId={sectionId}
              onAddItem={onAddItem}
              onAddSubItem={onAddSubItem}
              onUpdateTitle={onUpdateTitle}
              onUpdateResponseType={onUpdateResponseType}
              onMovePriority={onMovePriority}
              onDeleteItem={onDeleteItem}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Antes de salvar: troca quebras de linha do Windows por quebras normais e tira espacos e linhas vazias
// do FIM do texto. As quebras de linha que a pessoa fez de proposito (e as linhas em branco do meio) ficam.
function cleanMultiline(text) {
  return String(text || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/^\n+/, "")
    .replace(/\s+$/, "");
}

// Campo de texto que aceita VARIAS LINHAS: Enter cria uma linha nova (como no Procore).
// Salva ao clicar ou tocar fora. Ctrl/Cmd + Enter tambem salva. Esc cancela.
function EditableTitle({ value, onSave, className }) {
  const [text, setText] = useState(value);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  const skipSave = useRef(false);

  useEffect(() => {
    setText(value);
  }, [value]);

  // A caixa cresce sozinha conforme o texto, sem barra de rolagem.
  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const borders = el.offsetHeight - el.clientHeight; // a borda nao entra no scrollHeight: sem isso a ultima linha seria cortada
    el.style.height = `${el.scrollHeight + borders}px`;
  }, []);

  useLayoutEffect(() => {
    fit();
  }, [text, fit]);

  // Se a largura da tela mudar, as linhas quebram em outros lugares: ajusta a altura de novo.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== lastWidth) {
        lastWidth = el.clientWidth;
        fit();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  return (
    <div className="w-full min-w-0">
      <textarea
        ref={ref}
        rows={1}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (skipSave.current) {
            skipSave.current = false;
            return;
          }
          const cleaned = cleanMultiline(text);
          if (!cleaned) {
            setText(value); // um item nao pode ficar sem texto: volta ao que estava
            return;
          }
          if (cleaned !== text) setText(cleaned); // mostra o texto ja limpo, sem linhas vazias sobrando
          onSave(cleaned);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            skipSave.current = true;
            setText(value);
            e.currentTarget.blur();
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        style={{ overflow: "hidden", resize: "none" }}
        className={`block w-full whitespace-pre-wrap break-words leading-snug outline-none transition-colors ${className}`}
      />
      {focused && (
        <div className="px-1.5 pt-0.5 text-[10px] font-normal normal-case text-muted-foreground">
          Enter adds a new line. Click or tap outside to save.
        </div>
      )}
    </div>
  );
}

function buildTree(items) {
  const sortedItems = [...items].sort(
    (a, b) => (a.sequence || a.order || a.position || 0) - (b.sequence || b.order || b.position || 0)
  );
  const byId = {};
  const roots = [];
  sortedItems.forEach((it) => { byId[it.id] = { ...it, children: [] }; });
  sortedItems.forEach((it) => {
    const node = byId[it.id];
    if (it.parent_item_id && byId[it.parent_item_id]) {
      byId[it.parent_item_id].children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

const MAX_CLONE_ROWS = 50;
let cloneRowSeq = 0;
const newCloneRow = (title = "", number = "", location = "") => ({ key: ++cloneRowSeq, title, number, location });

// Gera os numeros das copias a partir do que foi digitado, mantendo o formato.
//   "10"    -> 11, 12, 13...      "G.09" -> G.10, G.11...      "WC-9" -> WC-10, WC-11...
//   "L1-01" -> L1-02, L1-03...    "009"  -> 010, 011...        "Level" (sem numero) -> Level 1, Level 2...
// startAfter = true: a primeira copia e o numero SEGUINTE ao digitado (10 -> 11).
// startAfter = false: a primeira copia usa o proprio numero digitado (10 -> 10, 11...).
function buildNumberSequence(base, count, startAfter) {
  const text = (base || "").trim();
  if (!text) return Array.from({ length: count }, () => "");
  const m = text.match(/^(.*?)(\d+)(\D*)$/); // o ULTIMO bloco de digitos do texto
  if (!m || m[2].length > 15) {
    return Array.from({ length: count }, (_, i) => `${text} ${i + 1}`);
  }
  const prefix = m[1];
  const digits = m[2];
  const suffix = m[3];
  const first = parseInt(digits, 10) + (startAfter ? 1 : 0);
  return Array.from({ length: count }, (_, i) => `${prefix}${String(first + i).padStart(digits.length, "0")}${suffix}`);
}

function CloneDialog({ template, projects, onClose }) {
  const [selectedJobsiteId, setSelectedJobsiteId] = useState(
    projects.length > 0 ? projects[0].id : ""
  );
  // Uma linha por inspecao a criar: nome, numero (ex: G.09) e localizacao (ex: Ground Floor).
  const [rows, setRows] = useState(() => [newCloneRow(template?.title || "")]);
  const [bulk, setBulk] = useState("5");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (projects.length > 0 && !selectedJobsiteId) {
      setSelectedJobsiteId(projects[0].id);
    }
  }, [projects, selectedJobsiteId]);

  const room = MAX_CLONE_ROWS - rows.length; // quantas linhas ainda cabem
  const lastNumber = rows.length > 0 ? rows[rows.length - 1].number : "";
  const bulkCount = Math.max(1, Math.min(room > 0 ? room : 1, parseInt(bulk, 10) || 1));
  const bulkNumbers = useMemo(() => buildNumberSequence(lastNumber, bulkCount, true), [lastNumber, bulkCount]);
  const bulkHasNumbers = bulkNumbers.some(Boolean);
  const bulkPreview = !bulkHasNumbers
    ? "same name, no numbers"
    : bulkCount <= 4
    ? `No. ${bulkNumbers.join(", ")}`
    : `No. ${bulkNumbers.slice(0, 3).join(", ")} … ${bulkNumbers[bulkCount - 1]}`;

  const updateRow = (key, field, value) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

  // A nova linha ja vem com o nome e a localizacao da anterior: normalmente so o numero muda.
  const addRow = () =>
    setRows((prev) => {
      if (prev.length >= MAX_CLONE_ROWS) return prev;
      const last = prev[prev.length - 1];
      return [...prev, newCloneRow(last ? last.title : template?.title || "", "", last ? last.location : "")];
    });

  // Varias de uma vez: copia o nome e a localizacao da ultima linha e continua a numeracao dela.
  const addBulk = () =>
    setRows((prev) => {
      const space = MAX_CLONE_ROWS - prev.length;
      if (space <= 0) return prev;
      const n = Math.min(space, Math.max(1, parseInt(bulk, 10) || 1));
      const last = prev[prev.length - 1];
      const numbers = buildNumberSequence(last ? last.number : "", n, true);
      return [
        ...prev,
        ...numbers.map((num) => newCloneRow(last ? last.title : template?.title || "", num, last ? last.location : "")),
      ];
    });

  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const handleClone = async () => {
    if (!selectedJobsiteId) {
      toast.error("Please select a target jobsite project.");
      return;
    }
    const prepared = rows.map((r) => ({
      title: r.title.trim(),
      number: r.number.trim(),
      location: r.location.trim(),
    }));
    if (prepared.some((r) => !r.title)) {
      toast.error("Every inspection needs a name.");
      return;
    }

    setLoading(true);
    setProgress(0);
    let done = 0;

    try {
      for (const r of prepared) {
        await api.post(`/itp/templates/${template.id}/clone`, {
          project_id: selectedJobsiteId,
          title: r.title,
          number: r.number,
          location: r.location,
        });
        done += 1;
        setProgress(done);
      }

      toast.success(
        done === 1
          ? `Inspection "${prepared[0].title}" created!`
          : `${done} inspections created!`
      );
      onClose();
    } catch (e) {
      // As linhas que ja foram criadas saem da lista, para nao criar duas vezes ao tentar de novo.
      if (done > 0) setRows((prev) => prev.slice(done));
      const reason = e.response?.data?.detail || "Failed to clone template.";
      toast.error(done > 0 ? `Created ${done} of ${prepared.length}. ${reason}` : reason);
    } finally {
      setLoading(false);
    }
  };

  const gridCols = "md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,1fr)_2.5rem]";
  const fieldClass = "h-11 rounded-lg border-border bg-background text-base md:h-9 md:rounded-sm md:text-xs";

  return (
    <Dialog open onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-xl border-border bg-card text-foreground md:max-w-3xl md:rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Copy className="h-4 w-4 shrink-0 text-primary" />
            <span className="break-words">Clone Template — {template?.title}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Choose the project, then add one line for each inspection you want to create.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium md:text-xs">Target Jobsite Project</Label>
            {projects.length === 0 ? (
              <p className="text-xs font-mono text-amber-500">No projects available.</p>
            ) : (
              <select
                value={selectedJobsiteId}
                onChange={(e) => setSelectedJobsiteId(e.target.value)}
                disabled={loading}
                className="h-12 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground focus:outline-none focus:ring-1 focus:ring-primary md:h-9 md:rounded-sm md:text-xs"
              >
                {projects.map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {proj.name || proj.title || `Project #${proj.id}`}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium md:text-xs">Inspections to create</Label>
              <span className="text-xs text-muted-foreground">
                {rows.length} of {MAX_CLONE_ROWS}
              </span>
            </div>

            {/* Titulos das colunas: so no computador */}
            <div className={`hidden gap-2 px-0.5 text-xs font-medium text-muted-foreground md:grid ${gridCols}`}>
              <span>Name</span>
              <span>No.</span>
              <span>Location</span>
              <span />
            </div>

            {rows.map((row, idx) => (
              <div
                key={row.key}
                className={`space-y-2 rounded-lg border border-border bg-background/40 p-3 md:grid md:items-center md:gap-2 md:space-y-0 md:border-0 md:bg-transparent md:p-0 ${gridCols}`}
              >
                <div className="flex items-center justify-between md:hidden">
                  <span className="text-xs font-semibold text-muted-foreground">Inspection {idx + 1}</span>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(row.key)}
                      disabled={loading}
                      aria-label={`Remove inspection ${idx + 1}`}
                      className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:text-destructive disabled:opacity-40"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-1 md:space-y-0">
                  <Label className="text-xs text-muted-foreground md:sr-only">Name</Label>
                  <Input
                    value={row.title}
                    onChange={(e) => updateRow(row.key, "title", e.target.value)}
                    placeholder="e.g. Window Setdowns - WPM 202.1"
                    disabled={loading}
                    className={fieldClass}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2 md:contents">
                  <div className="space-y-1 md:space-y-0">
                    <Label className="text-xs text-muted-foreground md:sr-only">No.</Label>
                    <Input
                      value={row.number}
                      onChange={(e) => updateRow(row.key, "number", e.target.value)}
                      placeholder="e.g. G.09"
                      disabled={loading}
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-1 md:space-y-0">
                    <Label className="text-xs text-muted-foreground md:sr-only">Location</Label>
                    <Input
                      value={row.location}
                      onChange={(e) => updateRow(row.key, "location", e.target.value)}
                      placeholder="e.g. Ground Floor"
                      disabled={loading}
                      className={fieldClass}
                    />
                  </div>
                </div>

                <div className="hidden md:block">
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(row.key)}
                      disabled={loading}
                      aria-label={`Remove inspection ${idx + 1}`}
                      className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:text-destructive disabled:opacity-40"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addRow}
              disabled={loading || room <= 0}
              className="h-11 w-full rounded-lg border-dashed text-sm md:h-9 md:w-auto md:rounded-sm md:text-xs"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add another inspection
            </Button>

            {/* Para muitas de uma vez */}
            <div className="space-y-2 rounded-lg border border-border bg-background/40 p-3 md:rounded-sm">
              <div className="text-sm font-medium md:text-xs">Need a lot? Add many at once</div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max={Math.max(1, room)}
                  value={bulk}
                  onChange={(e) => setBulk(e.target.value)}
                  onBlur={() => setBulk(String(bulkCount))}
                  disabled={loading || room <= 0}
                  aria-label="How many copies to add"
                  className="h-11 w-24 rounded-lg border-border bg-background text-base md:h-9 md:rounded-sm md:text-xs"
                />
                <Button
                  type="button"
                  onClick={addBulk}
                  disabled={loading || room <= 0}
                  className="h-11 flex-1 rounded-lg text-sm md:h-9 md:flex-none md:rounded-sm md:px-4 md:text-xs"
                >
                  <Copy className="mr-1.5 h-4 w-4" /> Add {bulkCount} {bulkCount === 1 ? "copy" : "copies"}
                </Button>
              </div>
              <p className="break-words text-xs text-muted-foreground">
                {room <= 0
                  ? `The limit is ${MAX_CLONE_ROWS} per time.`
                  : `Copies the last line and keeps counting from its No.: ${bulkPreview}.`}
              </p>
            </div>

            <p className="text-[11px] text-muted-foreground">
              The name starts as the template name. No. and Location are optional, and you can change them later.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="h-12 rounded-lg text-base md:h-9 md:rounded-sm md:text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={handleClone}
            disabled={loading || projects.length === 0}
            className="h-12 rounded-lg bg-primary text-base hover:bg-primary/90 md:h-9 md:rounded-sm md:text-xs"
          >
            {loading ? `Creating ${Math.min(progress + 1, rows.length)} of ${rows.length}...` : `Create (${rows.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
